/**
 * `kuika://` links inside assistant chat messages.
 *
 * The chat panel's "Couldn't do" section names the page each unfinished item belongs
 * to. Naming it is not enough — the designer's own validation-errors list has had
 * click-to-navigate for years (`design-footer.tsx` → `loadScreen`), and a page name
 * the user has to go hunt for is a worse affordance than the one right next to it.
 *
 * mcp-chat-server emits `[PageName](kuika://screen/<id-or-name>)` (see
 * `pipeline/handoff/unfinished-finding.ts`, which documents the same scheme constant
 * on its side). This module is the client half: parse the scheme, resolve the
 * reference against the LIVE screen list, and navigate.
 *
 * Resolution accepts an id OR a name on purpose. The server prefers the id because it
 * survives a rename, but a screen the turn did not create may never have had its id
 * threaded to the producer — and the validation-errors list proves name-only
 * navigation is workable (`IValidationsModel` carries `screenName` and no id at all).
 * Resolving against the live list at CLICK time also means a message read days later
 * still lands correctly.
 */

import type { IScreenDesignInfo } from "@kuika/kuika-cl-model";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { openDesignerScreen } from "./designer-screen-actions";

/**
 * Must stay identical to `SCREEN_LINK_SCHEME` in
 * `mcp-chat-server/src/pipeline/handoff/unfinished-finding.ts`.
 *
 * Duplicated rather than shared because the two packages cannot import each other, and
 * putting a cosmetic string in `kuika-cl-model` would place it behind that package's
 * Top-risk schema-evolution bar. Both sides name each other; change one, change both.
 */
export const SCREEN_LINK_SCHEME: string = "kuika://screen/";

/**
 * Must stay identical to `ASK_LINK_SCHEME` in
 * `mcp-chat-server/src/pipeline/handoff/unfinished-finding.ts`. Same duplication rationale as
 * `SCREEN_LINK_SCHEME` above.
 *
 * Each "Couldn't do" line ends with `[How do I do this?](kuika://ask/<encoded question>)`. The
 * whole question is in the href because the server owns the localized strings — reconstructing it
 * here would be a second copy of those templates to keep in sync, in a package that cannot import
 * them.
 */
export const ASK_LINK_SCHEME: string = "kuika://ask/";

interface ScreenDesignBrowserWindow extends Window {
  getSdInstance?: () => ScreenDesignInstance | undefined;
}

/**
 * The designer runtime, typed.
 *
 * The chat panel already declares this shape file-locally; the message component
 * reached the same global through `(window as any).getSdInstance?.()`, which the repo's
 * no-`any` rule forbids. One typed accessor beside the link helpers serves both.
 */
export function getChatDesignerRuntime(): ScreenDesignInstance | undefined {
  return (window as ScreenDesignBrowserWindow).getSdInstance?.();
}

/**
 * Schemes that must never reach a live `href` in a panel that renders model output.
 *
 * `blob:` is deliberately absent: the chat panel turns `blob:` links into a download
 * card BEFORE this check runs, which is why the board's own `LINK_BLOCK`
 * (`components/management/board/safe-markdown.tsx`) cannot be reused verbatim — it
 * blocks `blob:` too.
 */
const DANGEROUS_SCHEME: RegExp = /^(?:javascript|data|vbscript|file):/;

/**
 * Whitespace and invisible control/format characters a scheme can be smuggled through
 * (`java\nscript:`). Same expression the board's sanitizer uses.
 */
const STRIP_CHARS: RegExp = /[\s\p{Cc}\p{Cf}]/gu;

/**
 * True when this href would execute script or read a local file if rendered as an
 * anchor. The chat panel passed EVERY href through untouched before this existed.
 */
export function isDangerousChatHref(href: string | undefined): boolean {
  const cleaned: string = (href ?? "").replace(STRIP_CHARS, "").toLowerCase();
  return DANGEROUS_SCHEME.test(cleaned);
}

/**
 * The screen id-or-name a `kuika://screen/…` href points at, or `null` when the href is
 * not a screen link. Percent-decoding is best-effort — a malformed escape yields the
 * raw segment rather than throwing inside a render path.
 */
export function parseScreenLinkRef(href: string | undefined): string | null {
  const raw: string = (href ?? "").trim();
  if (!raw.toLowerCase().startsWith(SCREEN_LINK_SCHEME)) return null;
  const segment: string = raw.slice(SCREEN_LINK_SCHEME.length);
  if (segment.length === 0) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * The question a `kuika://ask/…` href carries, or `null` when the href is not an ask link.
 *
 * Percent-decoding is best-effort for the same reason as `parseScreenLinkRef`: a malformed escape
 * must degrade, not throw inside a render path.
 */
export function parseAskLinkRef(href: string | undefined): string | null {
  const raw: string = (href ?? "").trim();
  if (!raw.toLowerCase().startsWith(ASK_LINK_SCHEME)) return null;
  const segment: string = raw.slice(ASK_LINK_SCHEME.length);
  if (segment.length === 0) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    decoded = segment;
  }
  const question: string = decoded.trim();
  if (question.length === 0) return null;
  return question;
}

/**
 * Resolve an id-or-name against the live screen list.
 *
 * Case-insensitive on the name, matching `handleNavigateToScreen`'s `/screen` command
 * and the rest of the designer. NOTE: plain `toLowerCase()` does not fold the Turkish
 * dotted capital İ, so a screen named `İşlemler` will not match `işlemler` — the whole
 * designer behaves this way today, and preferring the id in the href is what sidesteps
 * it. Widening the fold here alone would make this resolver disagree with every other
 * one in the codebase.
 */
export function resolveScreenRef(sdInstance: ScreenDesignInstance, ref: string): IScreenDesignInfo | undefined {
  const screenList: IScreenDesignInfo[] = getValues(sdInstance)?.screenList ?? [];
  if (screenList.length === 0) return undefined;
  const needle: string = ref.trim();
  const lowered: string = needle.toLowerCase();
  return screenList.find(
    (screen: IScreenDesignInfo): boolean => screen.id?.toString() === needle || screen.name?.toLowerCase() === lowered
  );
}

export interface ScreenLinkNavigationResult {
  ok: boolean;
  screenName?: string;
  error?: string;
}

/**
 * Open the referenced screen on the canvas.
 *
 * `openDesignerScreen`'s `abortOnSaveFailure` is left at its default `false` — its own
 * doc comment reserves the `true` variant for programmatic (MCP) callers, because a
 * human already gets a warning toast and must stay free to navigate away. This is a
 * human click.
 */
export async function navigateToScreenRef(
  sdInstance: ScreenDesignInstance,
  ref: string
): Promise<ScreenLinkNavigationResult> {
  const match: IScreenDesignInfo | undefined = resolveScreenRef(sdInstance, ref);
  if (match === undefined) return { ok: false, error: `Screen "${ref}" was not found in this project.` };
  const result = await openDesignerScreen(sdInstance, match.id?.toString() ?? "");
  if ("error" in result) return { ok: false, screenName: match.name, error: result.error };
  return { ok: true, screenName: result.screenName };
}
